import { useMemo } from 'react';
import { useGetGroupEventsV2Query } from '~/store/services/groupClass';
import { getDurationInMinutes } from '../util/calendar.util';
import { AppointmentTimeIcon } from '../constants/appointment.constants';
import { isScheduledItemRecurring } from '../util/schedule/isScheduledItemRecurring';
import { showGroupClassEventZoomButton } from '../util/classes/groupClasses.util';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ScheduleType } from '../util/schedule/schedule.util';
import { useGetGroupSectionNotesByEventExternalIdsQuery } from '~/store/services/notes/notes';
import type { GetGroupClassNoteByEventExternalIdResponse } from '../types/notes/GetGroupClassNoteByEventExternalIdResponse';
import { NoteStatus } from '../constants/AddPatientNoteModal.constants';
import { ParticipantTypes } from '../constants/schedule.constants';

interface ConsolidatedScheduleItemsProps {
  scheduleItems: ScheduleItemBase[];
  providerId?: string;
  patientId?: string;
  filterByValue: string;
  startDateTime: string;
  // Optional so an open-ended "upcoming" caller can omit it; the V2 events
  // query already treats a missing end date as unbounded.
  endDateTime?: string;
  pageSize?: number;
  shouldIncludeNoteStatus?: boolean;
}

export const useConsolidatedScheduleItems = ({
  scheduleItems,
  providerId,
  patientId,
  filterByValue,
  startDateTime,
  endDateTime,
  pageSize,
  shouldIncludeNoteStatus = true,
}: ConsolidatedScheduleItemsProps): {
  consolidatedScheduleItems: ScheduleItemBase[];
  isGroupEventsError: boolean;
  isLoading: boolean;
} => {
  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const getScheduleEventsFromGroupEventsV2 = ({
    groupEventsV2,
    userId,
    notes,
  }: {
    groupEventsV2: GroupEventV2[];
    userId: string;
    notes: GetGroupClassNoteByEventExternalIdResponse[];
  }): ScheduleItemBase[] => {
    if (!groupEventsV2 || groupEventsV2.length === 0) return [];

    const notesByEventId = new Map(
      notes
        .filter((note) => !!note.groupSectionEventExternalId)
        .map(
          (note) => [note.groupSectionEventExternalId as string, note] as const,
        ),
    );

    const getNotes = (eventId: string) => {
      const note = notesByEventId.get(eventId);

      return {
        noteExternalId: note?.noteExternalId,
        noteStatus: note?.noteStatus,
        isNoteInDraftState: note?.noteStatus === NoteStatus.DRAFT,
        isNoteInSignedState: note?.noteStatus === NoteStatus.SIGNED,
      };
    };

    const groupEvents = groupEventsV2.map((event) => {
      const hosts: ScheduleUserBase[] = event.hosts.map((host) => {
        const {
          externalId,
          firstName,
          lastName,
          displayFirstName,
          displayLastName,
          providerType,
        } = host;
        return {
          externalId,
          firstName,
          lastName,
          displayFirstName,
          displayLastName,
          type: ParticipantTypes.PROVIDER,
          providerType,
        };
      });

      const isCurrentUserHost = event.hosts.some(
        (host) => host.externalId === userId,
      );

      return {
        attendees: [],
        color: 'marigold',
        declinedUsers: [],
        duration: getDurationInMinutes(event.startTime, event.endTime),
        endDateTime: event.endTime,
        externalId: event.id,
        frequency: event.frequency,
        groupClassExternalId: event.groupId,
        hosts,
        icon: AppointmentTimeIcon.GROUP,
        isRecurring: isScheduledItemRecurring(event.frequency),
        isShowZoomButton: showGroupClassEventZoomButton({
          end: event.endTime,
          isAttendee: false,
          isHost: isCurrentUserHost,
          start: event.startTime,
        }),
        startDateTime: event.startTime,
        status: 'scheduled',
        title: event.title,
        length: event.length,
        currentSession: event.currentSession,
        registrantsCount: event.registrantsCount,
        typeOf: ScheduleType.GROUP_CLASS_EVENT,
        ...getNotes(event.id),
      } as ScheduleItemBase;
    });

    return groupEvents;
  };

  const {
    data: groupEventsData,
    isError: isGroupEventsError,
    isFetching: isGroupEventsFetching,
  } = useGetGroupEventsV2Query(
    {
      endDateTime,
      ...(providerId ? { providerExternalId: providerId } : {}),
      ...(patientId ? { patientExternalId: patientId } : {}),
      ...(pageSize ? { pageSize } : {}),
      showDetailed: false,
      startDateTime,
    },
    { skip: !isGroupsRestructuringEnabled },
  );

  const groupSectionEventExternalIds = useMemo(
    () => groupEventsData?.map((event) => event.id) ?? [],
    [groupEventsData],
  );

  const {
    data: groupSectionNotesData,
    isFetching: isGroupSectionNotesFetching,
  } = useGetGroupSectionNotesByEventExternalIdsQuery(
    groupSectionEventExternalIds,
    {
      skip:
        !isGroupsRestructuringEnabled ||
        !shouldIncludeNoteStatus ||
        groupSectionEventExternalIds.length === 0,
    },
  );

  const consolidatedScheduleItems = useMemo<ScheduleItemBase[]>(() => {
    if (!isGroupsRestructuringEnabled) {
      return scheduleItems;
    }

    const appointmentItems = scheduleItems.filter(
      (item) => !item.groupClassExternalId,
    );

    const groupEventItems = getScheduleEventsFromGroupEventsV2({
      groupEventsV2: groupEventsData ?? [],
      userId: providerId ?? filterByValue,
      notes: groupSectionNotesData ?? [],
    });

    return [...appointmentItems, ...groupEventItems].sort(
      (a, b) =>
        new Date(a.startDateTime).getTime() -
        new Date(b.startDateTime).getTime(),
    );
  }, [
    scheduleItems,
    groupEventsData,
    providerId,
    filterByValue,
    isGroupsRestructuringEnabled,
    groupSectionNotesData,
  ]);

  const isLoading = useMemo(
    () => isGroupEventsFetching || isGroupSectionNotesFetching,
    [isGroupEventsFetching, isGroupSectionNotesFetching],
  );

  return {
    consolidatedScheduleItems,
    isGroupEventsError,
    isLoading,
  };
};
